import api from './api';

// Tipos de documentos disponíveis
export const TIPOS_DOCUMENTO = [
  { value: 'comprovante_residencia', label: 'Comprovante de Residência' },
  { value: 'historico_escolar', label: 'Histórico Escolar' },
  { value: 'identidade', label: 'Identidade (RG)' },
  { value: 'cpf', label: 'CPF' },
  { value: 'certidao_nascimento', label: 'Certidão de Nascimento' },
  { value: 'certidao_casamento', label: 'Certidão de Casamento' },
];

// Status dos documentos
export const STATUS_DOCUMENTO = {
  PENDENTE: 'pendente',
  AGUARDANDO_REVISAO: 'aguardando_revisao',
  APROVADO: 'aprovado',
  REJEITADO: 'rejeitado',
};

// Limite de tamanho de arquivo (2MB)
export const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB em bytes
export const MAX_FILE_SIZE_MB = 2;

export const documentoAlunoService = {
  /**
   * Upload de documento para um aluno
   * @param {string} alunoId - ID do aluno (pessoa)
   * @param {File} file - Arquivo a ser enviado
   * @param {string} tipoDocumento - Tipo do documento
   * @returns {Promise<Object>}
   */
  uploadDocumento: async (alunoId, file, tipoDocumento) => {
    try {
      // Validar tamanho do arquivo antes do upload
      if (file.size > MAX_FILE_SIZE) {
        return {
          success: false,
          message: `O arquivo excede o limite de ${MAX_FILE_SIZE_MB}MB. Tamanho atual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
        };
      }

      const formData = new FormData();
      formData.append('documento', file);
      formData.append('tipoDocumento', tipoDocumento);

      const response = await api.post(`/alunos/${alunoId}/documentos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Documento enviado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao fazer upload de documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao enviar documento'
      };
    }
  },

  /**
   * Buscar documentos de um aluno
   * @param {string} alunoId - ID do aluno (pessoa)
   * @returns {Promise<Object>}
   */
  getDocumentos: async (alunoId) => {
    try {
      const response = await api.get(`/alunos/${alunoId}/documentos`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Erro ao carregar documentos'
      };
    }
  },

  /**
   * Remover documento
   * @param {string} documentoId - ID do documento
   * @returns {Promise<Object>}
   */
  removeDocumento: async (documentoId) => {
    try {
      const response = await api.delete(`/alunos/documentos/${documentoId}`);
      return {
        success: true,
        message: response.data.message || 'Documento removido com sucesso'
      };
    } catch (error) {
      console.error('Erro ao remover documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover documento'
      };
    }
  },

  /**
   * Aprovar documento (apenas para usuários com nível > 3)
   * @param {string} documentoId - ID do documento
   * @returns {Promise<Object>}
   */
  aprovarDocumento: async (documentoId) => {
    try {
      const response = await api.put(`/alunos/documentos/${documentoId}/aprovar`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Documento aprovado com sucesso'
      };
    } catch (error) {
      console.error('Erro ao aprovar documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao aprovar documento'
      };
    }
  },

  /**
   * Rejeitar documento (apenas para usuários com nível > 3)
   * @param {string} documentoId - ID do documento
   * @param {string} motivoRejeicao - Motivo da rejeição
   * @returns {Promise<Object>}
   */
  rejeitarDocumento: async (documentoId, motivoRejeicao) => {
    try {
      const response = await api.put(`/alunos/documentos/${documentoId}/rejeitar`, {
        motivoRejeicao
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Documento rejeitado'
      };
    } catch (error) {
      console.error('Erro ao rejeitar documento:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao rejeitar documento'
      };
    }
  },

  /**
   * Buscar documentos pendentes de revisão (para usuários com nível > 3)
   * @param {Object} params - Parâmetros de busca
   * @returns {Promise<Object>}
   */
  getDocumentosPendentes: async (params = {}) => {
    try {
      const response = await api.get('/alunos/documentos/pendentes', { params });
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Erro ao buscar documentos pendentes:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Erro ao carregar documentos pendentes'
      };
    }
  },

  /**
   * Obter URL do documento para visualização
   * @param {string} nomeArquivo - Nome do arquivo
   * @returns {string}
   */
  getDocumentoUrl: (nomeArquivo) => {
    if (!nomeArquivo) return null;
    
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3001/api'
      : 'https://api.ibe.elshadaysystems.com.br/api';
    return `${baseUrl}/alunos/documentos/arquivo/${nomeArquivo}`;
  },

  /**
   * Obter documento como blob autenticado
   * @param {string} nomeArquivo - Nome do arquivo
   * @returns {Promise<string|null>}
   */
  getDocumentoBlob: async (nomeArquivo) => {
    try {
      if (!nomeArquivo || nomeArquivo === 'undefined' || nomeArquivo === 'null') {
        console.warn('Nome de arquivo inválido:', nomeArquivo);
        return null;
      }

      const response = await api.get(`/alunos/documentos/arquivo/${nomeArquivo}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Erro ao obter documento:', error);
      return null;
    }
  },

  /**
   * Validar arquivo antes do upload
   * @param {File} file - Arquivo a ser validado
   * @returns {Object}
   */
  validateFile: (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp'
    ];

    if (!file) {
      return { valid: false, error: 'Nenhum arquivo selecionado' };
    }

    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return { valid: false, error: `Arquivo muito grande (${fileSizeMB}MB). Máximo permitido: ${MAX_FILE_SIZE_MB}MB` };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Tipo de arquivo não permitido. Use PDF ou imagens (JPG, PNG, GIF, WebP)' };
    }

    return { valid: true };
  },

  /**
   * Obter label do tipo de documento
   * @param {string} tipo - Tipo do documento
   * @returns {string}
   */
  getTipoLabel: (tipo) => {
    const tipoEncontrado = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    return tipoEncontrado ? tipoEncontrado.label : tipo;
  },

  /**
   * Obter cor do status
   * @param {string} status - Status do documento
   * @returns {string}
   */
  getStatusColor: (status) => {
    switch (status) {
      case STATUS_DOCUMENTO.PENDENTE:
        return 'text-gray-600 bg-gray-100';
      case STATUS_DOCUMENTO.AGUARDANDO_REVISAO:
        return 'text-yellow-700 bg-yellow-100';
      case STATUS_DOCUMENTO.APROVADO:
        return 'text-green-700 bg-green-100';
      case STATUS_DOCUMENTO.REJEITADO:
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  /**
   * Obter label do status
   * @param {string} status - Status do documento
   * @returns {string}
   */
  getStatusLabel: (status) => {
    switch (status) {
      case STATUS_DOCUMENTO.PENDENTE:
        return 'Pendente';
      case STATUS_DOCUMENTO.AGUARDANDO_REVISAO:
        return 'Aguardando Revisão';
      case STATUS_DOCUMENTO.APROVADO:
        return 'Aprovado';
      case STATUS_DOCUMENTO.REJEITADO:
        return 'Rejeitado';
      default:
        return status;
    }
  }
};

export default documentoAlunoService;
